import { Injectable } from '@angular/core';
import {
  AuthenticatedExecutionContext,
  AuthenticatedProfileId,
} from 'src/core/context/execution-context.types';
import {
  ProfileDefinition,
  ProfileNavigationItem,
  ProfileNavigationKey,
  ProfileRouteOwnership,
} from './profile-navigation.types';

const MIXER_PROFILES: readonly AuthenticatedProfileId[] = [
  'HORMIGON_MIXER',
  'ARIDOS_MIXER',
];

export const PROFILE_NAVIGATION: readonly ProfileNavigationItem[] = [
  { key: 'dashboard', path: '/dashboard', label: 'Dashboard', profiles: MIXER_PROFILES },
  {
    key: 'checklist',
    path: '/checklist',
    label: 'Check list',
    profiles: ['HORMIGON_MIXER'],
  },
  {
    key: 'dispatch',
    path: '/pedidospendiente',
    label: 'Despacho actual',
    profiles: MIXER_PROFILES,
  },
  {
    key: 'otherDispatches',
    path: '/otrosdespachos',
    label: 'Otros despachos',
    profiles: ['HORMIGON_MIXER'],
  },
  {
    key: 'history',
    path: '/pedidosEntregados',
    label: 'Historial',
    profiles: MIXER_PROFILES,
  },
  {
    key: 'fatigue',
    path: '/fatiga',
    label: 'Fatiga',
    profiles: ['HORMIGON_MIXER'],
  },
  {
    key: 'fuel',
    path: '/hormigon/controlCombustible',
    label: 'Control de combustible',
    profiles: ['HORMIGON_OPERADOR_PLANTA'],
  },
  {
    key: 'daily-close',
    path: '/hormigon-operador-planta/cierre-diario',
    label: 'Cierre diario',
    profiles: ['HORMIGON_OPERADOR_PLANTA'],
  },
  {
    key: 'fuel',
    path: '/aridos/controlCombustible',
    label: 'Control de combustible',
    profiles: ['ARIDOS_OPERADOR_PLANTA'],
  },
];

/** Registro único de los cuatro perfiles autenticados. */
export const PROFILE_DEFINITIONS: readonly ProfileDefinition[] = [
  {
    profileId: 'HORMIGON_MIXER',
    label: 'Hormigón Mixer',
    initialRoute: '/dashboard',
    navigation: [
      'dashboard',
      'checklist',
      'dispatch',
      'otherDispatches',
      'history',
      'fatigue',
    ],
  },
  {
    profileId: 'HORMIGON_OPERADOR_PLANTA',
    label: 'Hormigón Operador de Planta',
    initialRoute: '/hormigon/controlCombustible',
    navigation: ['fuel', 'daily-close'],
  },
  {
    profileId: 'ARIDOS_MIXER',
    label: 'Áridos Mixer',
    initialRoute: '/dashboard',
    navigation: ['dashboard', 'dispatch', 'history'],
  },
  {
    profileId: 'ARIDOS_OPERADOR_PLANTA',
    label: 'Áridos Operador de Planta',
    initialRoute: '/aridos/controlCombustible',
    navigation: ['fuel'],
  },
];

/**
 * Registro de ownership macro de las rutas visibles por perfil.
 */
export const PROFILE_ROUTE_OWNERSHIP: readonly ProfileRouteOwnership[] = [
  { path: '/dashboard', owner: 'authenticated-shell', channel: 'authenticated' },
  { path: '/editarperfil', owner: 'authenticated-shell', channel: 'authenticated' },
  { path: '/cambiarpassword', owner: 'authenticated-shell', channel: 'authenticated' },
  { path: '/notificaciones', owner: 'authenticated-shell', channel: 'authenticated' },
  { path: '/pedidospendiente', owner: 'mixer-profiles', channel: 'authenticated' },
  { path: '/otrosdespachos', owner: 'HORMIGON_MIXER', channel: 'authenticated' },
  {
    path: '/PedidosPendientes/Detalle',
    owner: 'mixer-profiles',
    channel: 'authenticated',
  },
  { path: '/pedidosEntregados', owner: 'mixer-profiles', channel: 'authenticated' },
  { path: '/checklist', owner: 'HORMIGON_MIXER', channel: 'authenticated' },
  { path: '/fatiga', owner: 'HORMIGON_MIXER', channel: 'authenticated' },
  {
    path: '/hormigon/controlCombustible',
    owner: 'operator-profiles',
    channel: 'authenticated',
  },
  {
    path: '/hormigon-operador-planta/cierre-diario',
    owner: 'HORMIGON_OPERADOR_PLANTA',
    channel: 'authenticated',
  },
  {
    path: '/aridos/controlCombustible',
    owner: 'operator-profiles',
    channel: 'authenticated',
  },
  { path: '/Seguimiento', owner: 'anonymous-shared', channel: 'anonymous' },
  { path: '/seguimiento-anonimo', owner: 'anonymous-shared', channel: 'anonymous' },
  { path: '/Seguimiento/3', owner: 'anonymous-shared', channel: 'anonymous' },
];

@Injectable({ providedIn: 'root' })
export class ProfileNavigationService {
  getProfiles(): readonly ProfileDefinition[] {
    return PROFILE_DEFINITIONS;
  }

  getProfile(profileId: AuthenticatedProfileId): ProfileDefinition | null {
    return PROFILE_DEFINITIONS.find((profile) => profile.profileId === profileId) ?? null;
  }

  getItems(): readonly ProfileNavigationItem[] {
    return PROFILE_NAVIGATION;
  }

  isVisible(
    context: AuthenticatedExecutionContext | null,
    key: ProfileNavigationKey
  ): boolean {
    if (!context) return false;
    return PROFILE_NAVIGATION.some(
      (candidate) => candidate.key === key && candidate.profiles.includes(context.profileId)
    );
  }

  getPath(
    context: AuthenticatedExecutionContext | null,
    key: ProfileNavigationKey
  ): string | null {
    if (!context) return null;
    return PROFILE_NAVIGATION.find(
      (candidate) => candidate.key === key && candidate.profiles.includes(context.profileId)
    )?.path ?? null;
  }

  isAllowedRoute(
    context: AuthenticatedExecutionContext,
    url: string
  ): boolean {
    const path = this.normalizePath(url);

    if (this.isCommonAuthenticatedRoute(path)) return true;
    if (path === '/' || path === '') return true;

    // El detalle conserva su URL legacy y debe compartir ownership con despacho.
    if (path === '/pedidospendientes/detalle') {
      return MIXER_PROFILES.includes(context.profileId);
    }

    return PROFILE_NAVIGATION.some((candidate) => {
      const itemPath = this.normalizePath(candidate.path);
      return (
        (path === itemPath || path.startsWith(`${itemPath}/`)) &&
        candidate.profiles.includes(context.profileId)
      );
    });
  }

  getDefaultRoute(context: AuthenticatedExecutionContext | null): string {
    if (!context) return '/login';
    return this.getProfile(context.profileId)?.initialRoute ?? '/dashboard';
  }

  private isCommonAuthenticatedRoute(path: string): boolean {
    return (
      path === '/editarperfil' ||
      path === '/cambiarpassword' ||
      path === '/notificaciones' ||
      path === '/unauthorized'
    );
  }

  private normalizePath(url: string): string {
    const withoutQuery = (url || '').split('?')[0].split('#')[0];
    const withLeadingSlash = withoutQuery.startsWith('/')
      ? withoutQuery
      : `/${withoutQuery}`;
    return (
      withLeadingSlash
        .replace(/\/+/g, '/')
        .replace(/\/$/, '')
        .toLowerCase() || '/'
    );
  }
}
